import type { DataTableColumn } from 'mantine-datatable';
import type { z } from 'zod';

import { createColumnDef } from '#core/lib/formatters/column-formatters.tsx';
import { getNestedValue, hasNestedPath } from '#core/lib/utils/object.ts';

import type { ColumnConfigSchema } from './schema.ts';
import type { TableRendererProps } from './TableRenderer.tsx';
import type { TransformParams } from './types.ts';

/**
 * Builds columns from explicit column configuration.
 */
export function buildColumnsFromConfig(
  records: Record<string, unknown>[],
  columnConfig: z.infer<typeof ColumnConfigSchema>[],
  firstRecord: Record<string, unknown> | undefined,
): DataTableColumn[] {
  return columnConfig
    .filter((colConfig) => {
      // Skip entries with missing accessor (defensive check)
      if (!colConfig.accessor) {
        console.warn('DashboardTable: Column config missing accessor, skipping');
        return false;
      }
      if (!firstRecord || !hasNestedPath(firstRecord, colConfig.accessor)) {
        console.warn(`DashboardTable: Column '${colConfig.accessor}' not found in data, skipping`);
        return false;
      }
      return true;
    })
    .map((colConfig) => {
      const values = records.map((row) => getNestedValue(row, colConfig.accessor));
      const baseDef = createColumnDef(colConfig.accessor, values, { decimalScale: colConfig.decimalScale });
      const needsNestedAccess = colConfig.accessor.includes('.');

      return {
        ...baseDef,
        title: colConfig.title ?? baseDef.title,
        width: colConfig.width ?? baseDef.width,
        ...(needsNestedAccess && {
          render: (record: Record<string, unknown>) => {
            const value = getNestedValue(record, colConfig.accessor);
            return value !== null && value !== undefined ? String(value) : '';
          },
        }),
      };
    });
}

/**
 * Builds columns by auto-detecting from first record.
 */
export function buildColumnsFromRecord(
  records: Record<string, unknown>[],
  firstRecord: Record<string, unknown> | undefined,
): DataTableColumn[] {
  if (!firstRecord) {
    return [];
  }
  return Object.keys(firstRecord).map((key) =>
    createColumnDef(
      key,
      records.map((row) => row[key]),
    ),
  );
}

/**
 * Reorders columns to place pinned columns first.
 */
export function applyColumnPinning(
  columns: DataTableColumn[],
  pinnedColumns: string[],
): { columns: DataTableColumn[]; pinFirstColumn: boolean } {
  const pinnedSet = new Set(pinnedColumns);
  const { pinned, unpinned } = columns.reduce<{ pinned: DataTableColumn[]; unpinned: DataTableColumn[] }>(
    (acc, col) => {
      if (pinnedSet.has(col.accessor as string)) {
        acc.pinned.push(col);
      } else {
        acc.unpinned.push(col);
      }
      return acc;
    },
    { pinned: [], unpinned: [] },
  );

  return {
    columns: [...pinned, ...unpinned],
    pinFirstColumn: pinned.length > 0,
  };
}

/**
 * Transforms TableData props into TableRenderer props.
 */
export function transformToTableProps({
  records,
  columnConfig,
  pinnedColumns,
  title,
}: TransformParams): TableRendererProps {
  const firstRecord = records[0];

  // Build base columns from config or auto-detect
  const baseColumns =
    columnConfig && columnConfig.length > 0
      ? buildColumnsFromConfig(records, columnConfig, firstRecord)
      : buildColumnsFromRecord(records, firstRecord);

  // Apply pinning if specified
  const { columns, pinFirstColumn } =
    pinnedColumns && pinnedColumns.length > 0
      ? applyColumnPinning(baseColumns, pinnedColumns)
      : { columns: baseColumns, pinFirstColumn: false };

  // Combine index with Cognite space/externalId for stable row identification
  const transformedRecords = records.map((item, index) => ({
    id: `row-${index}-${(item.space as string) ?? ''}-${(item.externalId as string) ?? ''}`,
    ...item,
  }));

  return {
    title,
    columns,
    records: transformedRecords,
    pinFirstColumn,
  };
}

/**
 * Filter rows across all visible columns using case-insensitive text matching.
 */
export function filterRecords(records: Record<string, unknown>[], filterText: string): Record<string, unknown>[] {
  if (!filterText) {
    return records;
  }
  const searchTerm = filterText.toLowerCase();
  return records.filter((record) =>
    Object.values(record).some((value) => String(value).toLowerCase().includes(searchTerm)),
  );
}
